import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import lodashOrderBy from 'lodash/orderBy';
import { HandleType } from '@/enums/handle-type.enum';
import { ModelItem, MenuData } from './nodes.types';
import { flattenMenuData, filterNodesBySearch, filterNodesByInputOutputTypes } from './nodes.utils';

type OrderByTypeFilter = 'featured' | 'price_asc' | 'price_desc';
export type SourceType = 'tools' | 'models' | 'groups';
type SourceTypeFilter = SourceType[];
export const CREATED_BY = {
  ALL: 'all',
  WEAVE: 'weave',
  WORKSPACE: 'workspace',
  ME: 'me',
} as const;
export type CreatedByFilter = 'weave' | 'workspace' | 'me';

const initialFilters = {
  search: '',
  orderBy: 'featured' as OrderByTypeFilter,
  source: [] as SourceTypeFilter,
  createdBy: [] as CreatedByFilter[],
  extraFilters: {
    no_training_mode: false,
    commercially_safe: false,
  },
  inputTypes: [],
  outputTypes: [],
};

const initialState: NodesMenuState = {
  isFiltersActive: false,
  filters: initialFilters,
  isMenuDataLoaded: false,
  currentUserId: null,
  menu: {
    recent: { id: 'recent', children: [] },
    savedModels: { id: 'saved_models', children: [] },
    savedNodes: { id: 'saved_nodes', children: [] },
    subflows: { id: 'subflows', children: [] },
    tools: { id: 'tools', children: [] },
    imageModels: { id: 'image_models', children: [] },
    videoModels: { id: 'video_models', children: [] },
    threedModels: { id: 'threed_models', children: [] },
    audioModels: { id: 'audio_models', children: [] },
    customModels: { id: 'custom_models', children: [] },
  },
  filteredMenuData: [],
};

interface NodesMenuState {
  isFiltersActive: boolean;
  filters: {
    search: string;
    orderBy: OrderByTypeFilter;
    source: SourceTypeFilter;
    createdBy: CreatedByFilter[];
    inputTypes: HandleType[];
    outputTypes: HandleType[];
  };
  isMenuDataLoaded: boolean;
  currentUserId: string | null;
  menu: MenuData;
  filteredMenuData: ModelItem[];
}

interface NodeMenuActions {
  setSearch: (search: string) => void;
  setOrderBy: (orderBy: OrderByTypeFilter) => void;
  setSource: (source: SourceTypeFilter) => void;
  setCreatedBy: (createdBy: CreatedByFilter[]) => void;
  setInputTypes: (inputTypes: HandleType[]) => void;
  setOutputTypes: (outputTypes: HandleType[]) => void;
  setCurrentUserId: (userId: string | null) => void;
  resetFilters: () => void;
  setMenuData: (menu: MenuData) => void;
  setIsMenuDataLoaded: (isMenuDataLoaded: boolean) => void;
}

type NodeFiltersStore = NodesMenuState & NodeMenuActions;

export const useNodeFiltersStore = create<NodeFiltersStore>()(
  devtools(
    (set) => ({
      ...initialState,
      setSearch: (search: string) =>
        set((state) => {
          const newFilters = { ...state.filters, search };
          return {
            filters: newFilters,
            filteredMenuData: filterMenu(state.menu, newFilters, state.currentUserId),
            isFiltersActive: isFiltersActive(newFilters),
          };
        }),
      setOrderBy: (orderBy: OrderByTypeFilter) =>
        set((state) => {
          const newFilters = { ...state.filters, orderBy };
          return {
            filters: newFilters,
            filteredMenuData: filterMenu(state.menu, newFilters, state.currentUserId),
            isFiltersActive: isFiltersActive(newFilters),
          };
        }),
      setSource: (source: SourceTypeFilter) =>
        set((state) => {
          const newFilters = { ...state.filters, source };
          return {
            filters: newFilters,
            filteredMenuData: filterMenu(state.menu, newFilters, state.currentUserId),
            isFiltersActive: isFiltersActive(newFilters),
          };
        }),
      setCreatedBy: (createdBy: CreatedByFilter[]) =>
        set((state) => {
          const newFilters = { ...state.filters, createdBy };
          return {
            filters: newFilters,
            filteredMenuData: filterMenu(state.menu, newFilters, state.currentUserId),
            isFiltersActive: isFiltersActive(newFilters),
          };
        }),
      setInputTypes: (inputTypes: HandleType[]) =>
        set((state) => {
          const newFilters = { ...state.filters, inputTypes: [...inputTypes] };
          return {
            filters: newFilters,
            filteredMenuData: filterMenu(state.menu, newFilters, state.currentUserId),
            isFiltersActive: isFiltersActive(newFilters),
          };
        }),
      setOutputTypes: (outputTypes: HandleType[]) =>
        set((state) => {
          const newFilters = { ...state.filters, outputTypes: [...outputTypes] };
          return {
            filters: newFilters,
            filteredMenuData: filterMenu(state.menu, newFilters, state.currentUserId),
            isFiltersActive: isFiltersActive(newFilters),
          };
        }),
      setCurrentUserId: (userId: string | null) =>
        set((state) => {
          if (state.currentUserId === userId) return state;
          return {
            currentUserId: userId,
            filteredMenuData: filterMenu(state.menu, state.filters, userId),
          };
        }),
      resetFilters: () =>
        set((state) => ({
          filters: initialFilters,
          filteredMenuData: flattenMenuData(state.menu),
          isFiltersActive: false,
        })),
      setMenuData: (menu: MenuData) =>
        set((state) => ({
          menu,
          filteredMenuData: filterMenu(menu, state.filters, state.currentUserId),
        })),
      setIsMenuDataLoaded: (isMenuDataLoaded: boolean) => set(() => ({ isMenuDataLoaded })),
    }),
    {
      name: 'Node Filters Store',
      enabled: import.meta.env.DEV,
    },
  ),
);

const classifyCreatedBy = (item: ModelItem, currentUserId: string | null): CreatedByFilter => {
  if (!item.createdBy) {
    return CREATED_BY.WEAVE;
  }
  return item.createdBy === currentUserId ? CREATED_BY.ME : CREATED_BY.WORKSPACE;
};

const filterMenu = (
  menuData: MenuData,
  filters: NodeFiltersStore['filters'],
  currentUserId: string | null,
): ModelItem[] => {
  if (!filters) return [];
  const { search, orderBy, source, createdBy, inputTypes, outputTypes } = filters;
  // Recent items also appear in saved/catalog sections, but the server omits
  // `createdBy` on the Recent copy. Drop them so filtering reads the copy
  // that carries the field.
  const { recent: _recent, ...menuWithoutRecent } = menuData;
  let menuDataToFilter: Partial<MenuData> = {};
  if (source.length === 0) {
    menuDataToFilter = { ...menuWithoutRecent };
  } else {
    if (source.includes('tools')) {
      menuDataToFilter.tools = menuData.tools;
      menuDataToFilter.savedNodes = menuData.savedNodes;
    }
    if (source.includes('models')) {
      menuDataToFilter.imageModels = menuData.imageModels;
      menuDataToFilter.videoModels = menuData.videoModels;
      menuDataToFilter.threedModels = menuData.threedModels;
      menuDataToFilter.audioModels = menuData.audioModels;
      menuDataToFilter.customModels = menuData.customModels;
      menuDataToFilter.savedModels = menuData.savedModels;
    }
    if (source.includes('groups')) {
      menuDataToFilter.subflows = menuData.subflows;
    }
  }

  const flatList = flattenMenuData(menuDataToFilter);
  let filteredMenuData = filterNodesBySearch(flatList, search);
  if (createdBy.length > 0) {
    filteredMenuData = filteredMenuData.filter((item) => createdBy.includes(classifyCreatedBy(item, currentUserId)));
  }
  if (inputTypes.length) {
    filteredMenuData = filterNodesByInputOutputTypes(filteredMenuData, 'inputTypes', inputTypes);
  }
  if (outputTypes.length) {
    filteredMenuData = filterNodesByInputOutputTypes(filteredMenuData, 'outputTypes', outputTypes);
  }
  if (orderBy === 'price_asc' || orderBy === 'price_desc') {
    const sort = orderBy === 'price_asc' ? 'asc' : 'desc';
    filteredMenuData = lodashOrderBy(
      filteredMenuData,
      [(item) => (item.price == null ? 0 : item.price), (item) => (item.order == null ? 0 : item.order)],
      [sort, 'asc'],
    );
  }
  return filteredMenuData;
};

const isFiltersActive = (filters: NodeFiltersStore['filters']) => {
  const activeFilterValues = [
    filters.search,
    filters.orderBy !== 'featured',
    filters.source.length > 0,
    filters.createdBy.length > 0,
    filters.inputTypes.length,
    filters.outputTypes.length,
  ];
  return activeFilterValues.some(Boolean);
};
